import React, { useState, useMemo, useEffect } from 'react';
import Collapsible from 'react-collapsible';
import { FaArrowUp, FaInfoCircle, FaSort, FaSortUp, FaSortDown,FaSync, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { MdKeyboardArrowRight, MdKeyboardArrowLeft } from 'react-icons/md';
import moment from 'moment';
import ReactPaginate from 'react-paginate';
import Spinner from '../../../components/UI/Spinner/Spinner';
import api from '../../../axios-local-api';
import withErrorHandler from '../../../hoc/withErrorHandler/withErrorHandler';
import Box from '../../../hoc/Box/Box';
import classes from './PreviousSpeedCheckResult.css';

// Helper function for sorting strings/numbers/dates
const sortData = (data, sortConfig) => {
    if (!sortConfig.key) return data;
    if(!Array.isArray(data)){
        return data
    }
    const sortedData = [...data].sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];

        // Handle string comparison for non-numeric fields
        if (typeof valA === 'string') {
            valA = valA.toLowerCase();
            valB = valB.toLowerCase();
        }

        if (valA < valB) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (valA > valB) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });

    return sortedData;
};

const PreviousSpeedCheckResult = (props) => {
    const [upArrow, setUpArrow] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState({
        intialCount: 0,
        currentCount: 10,
        currentPage: 0, // Track current page index
    });
    const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
    const [persistedData, setPersistedData] = useState([]);

    useEffect(() => {
        if (props.speedCheckResults && Array.isArray(props.speedCheckResults) && props.speedCheckResults.length > 0) {
            setPersistedData(props.speedCheckResults);
        }
    }, [props.speedCheckResults]);



    // --- Search and Sort Logic ---
    const filteredAndSortedData = useMemo(() => {
        const dataToUse = (props.speedCheckResults && props.speedCheckResults.length > 0) ? props.speedCheckResults : persistedData;
        let filteredData = dataToUse;


        // 1. Filter data based on search term
        if (searchTerm) {
            const lowerCaseSearch = searchTerm.toLowerCase();
            filteredData = dataToUse?.filter(result => 
                result.sourceSystem?.toLowerCase().includes(lowerCaseSearch) 
            )}

        // 2. Sort the filtered data
        return sortData(filteredData, sortConfig);
        
    }, [props.speedCheckResults,persistedData, searchTerm, sortConfig]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        // Reset pagination when search changes
        setPagination({ intialCount: 0, currentCount: 10, currentPage: 0 });
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <FaSort size={12} color="gray" style={{ marginLeft: '5px' }} />;
        }
        if (sortConfig.direction === 'asc') {
            return <FaSortUp size={12} style={{ marginLeft: '5px' }} />;
        }
        return <FaSortDown size={12} style={{ marginLeft: '5px' }} />;
    };


    // --- Pagination Logic ---
    const handlePagination = (pageSelected) => {
        const limit = 10;
        const maxNumber = Number(pageSelected + 1) * limit;
        const minNumber = maxNumber - limit;
        setPagination({
            intialCount: minNumber,
            currentCount: maxNumber >= filteredAndSortedData.length ? filteredAndSortedData.length : maxNumber,
            currentPage: pageSelected
        });
    }

    const getTable = () => {
        const { intialCount, currentCount } = pagination;
        const totalCheckResults = filteredAndSortedData.length;
        const totalPages = Math.ceil(totalCheckResults / 10);
        
        // Slice the data for the current page
        const paginatedData = filteredAndSortedData.slice(intialCount, currentCount);
       
        return (
            
            <React.Fragment>
               
                
                <table className={classes.CheckResultTable} cellPadding="0" cellSpacing="0" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('timestamp')}>
                                Date & Time {getSortIcon('timestamp')}
                            </th>
                            <th onClick={() => requestSort('sourceSystem')}>
                                Source {getSortIcon('sourceSystem')}
                            </th>
                            <th onClick={() => requestSort('subscribedUpSpeed')}>
                                Subscribed Speed Up {getSortIcon('subscribedUpSpeed')}
                            </th>
                            <th onClick={() => requestSort('subscribedDownSpeed')}>
                                Subscribed Speed Down {getSortIcon('subscribedDownSpeed')}
                            </th>
                            <th onClick={() => requestSort('pass')}>
                                Result {getSortIcon('pass')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.map((checkResult, index) => {
                            // Ensure a unique key for list items, using requestId if available
                            const key = checkResult.requestId || index; 

                            const resultIcon = checkResult.pass === true ?
                                            <FaCheckCircle
                                              color={'green'}
                                              style={{ marginRight: '5px' }}
                                              fontSize={'20px'}
                                            /> : checkResult.pass === false ?
                                            <FaExclamationCircle
                                              color={'red'}
                                              style={{ marginRight: '5px' }}
                                              fontSize={'20px'}
                                            /> :
                                            null;
                            const color  =  checkResult.pass === true ? 'green' : checkResult.pass === false ? 'red' : '';        
                            const result = <div
                                            style={{ display: 'flex', alignItems: 'center' }}
                                          >
                                            {resultIcon}
                                            <span style={{ color:color }}>{checkResult.pass === true ? 'Pass' : checkResult.pass === false ? 'Fail' : 'N/A'}</span>
                                          </div>
                            return (
                                <tr key={key}>
                                    <td id='Date&Time' >{moment(checkResult.timestamp).format('ddd MM/DD/YYYY hh:mm A')}</td>

                                    <td><span id='Source'>{checkResult.sourceSystem}</span></td>

                                    <td><span id='SubscribedSpeedUp'>{checkResult.subscribedUpSpeed ? `${checkResult.subscribedUpSpeed} Mbps` : "N/A"}</span></td>

                                    <td><span id='SubscribedSpeedDown'>{checkResult.subscribedDownSpeed ? `${checkResult.subscribedDownSpeed} Mbps` : "N/A"}</span></td>

                                    <td>{result}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
                <div className={classes.paginationWrapper}>
                    <span id = 'STPageNumber' className={classes.paginationInfo}>{`Showing  ${pagination.currentPage + 1} of ${totalPages}`}</span>
                    <ReactPaginate
                        id='STPagination'
                        previousLabel={<MdKeyboardArrowLeft id='STPervious' size="25px" />}
                        nextLabel={<MdKeyboardArrowRight size="25px" id='STNext' />}
                        breakLabel={'...'}
                        breakClassName={'break-me'}
                        pageCount={totalPages}
                        marginPagesDisplayed={1}
                        pageRangeDisplayed={5}
                        onPageChange={(value) => handlePagination(value.selected)}
                        containerClassName={classes.pagination}
                        activeClassName={classes.paginationactive}
                        disabledClassName={classes.paginationDisable}
                        // Added initialPage and forcePage to work correctly with search/sort resets
                        initialPage={0} 
                        forcePage={pagination.currentPage}
                    />
                </div>
            </React.Fragment>
        )
    }


    const errorMessage = () => {
        let message = '';
        let displayWarningIcon = false;
    
        if (!props.speedCheckResults){
            message = 'No speed test history is available.';
        }else if(filteredAndSortedData.length === 0){
            message = 'No results found'; 
        }else{    
          message = 'Something went wrong. Please try again shortly.';
          displayWarningIcon = true;
        }
    
        return (
            <div className={[classes.alertMessageContainer, classes.alertError].join(' ')}>
            { displayWarningIcon &&  <span><FaInfoCircle color="red" size={30} /></span> }
            <span>{ message }</span>
            </div>
        ) 
    
      };
   
    
    // ... (rest of the component structure remains the same)
    return (
        <div className={classes.PreviousSpeedCheckResult}>
            
                    <Box>
                        <div className={classes.PreviousSpeedCheckResultCollapse}>
                            <div className={classes.TitleArea}>
                                <h3  id ='STHistoryComponent' style={{ paddingRight: 20, whiteSpace: 'pre' }}>
                                    Router Speed Test History
                                </h3>
                                <button 
                                onClick={()=>{
                                    props.onSetResultsLoaded(false)
                                    props.onSetResultsLoading(true)
                                const latestRequestId = persistedData[0]?.requestId || props.speedCheckResults?.[0]?.requestId;
                                    props.getSpeedCheckResults(latestRequestId)
                                }}
                               id="STRefreshbtn"
                               className={classes.RegularButton}
                             >
                                <FaSync />  Refresh
                             </button>
                     
                            </div>
                    
                            
                        </div>
                    </Box>
                
             {!props.resultsLoaded && <Spinner />}
                { props.resultsLoaded && !props.resultsLoading && 

                <div className={classes.CheckResultTableWrapper}>
                    {/* Search Input Field Added */}
                    <div className= {classes.sourcesearch} style={{ position: 'relative', display: 'flex', flexDirection: 'row', alignItems: 'center', borderBottom: '1px solid lightgrey', justifyContent: 'space-between', paddingBottom: '30px', paddingLeft: '16px'}}>
                    <div className={classes.searchWrapper}>
                        <input
                            id = 'STSearch'
                            type="text"
                            placeholder="Search By Source"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyDown={handleKeyDown}
                            className={classes.searchInput}
                        />
                    </div>
                    { props?.speedCheckResults && <div id= 'STLastRan' className={classes.contentLabel} style={{ paddingRight: '30px'}}>Last Ran: {moment(props.speedCheckResults[0]?.timestamp).format('ddd MM/DD/YYYY hh:mm A')}</div> }
                    </div>
                    {filteredAndSortedData?.length > 0 ? getTable() : errorMessage()}
                
                </div>}
            
        </div>
    )
}

export default (withErrorHandler(PreviousSpeedCheckResult, api));
